import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { isAdminUser, fetchWithAuth } from '../../../utils/api';
import { Navigate } from 'react-router-dom';
import IconWrapper from '../../ui/IconWrapper';
import { Heading } from '../../ui/heading';
import { Timestamp } from '../../ui/timestamp';
import { 
  FiArrowLeft,
  FiFileText, 
  FiClock, 
  FiCheckCircle, 
  FiXCircle, 
  FiPause,
  FiRefreshCw,
  FiEye,
  FiUsers,
  FiDownload,
  FiAlertCircle,
  FiPlay,
  FiLayers
} from 'react-icons/fi';

interface ImportSession {
  id: string;
  session_id: string;
  filename: string;
  file_type: string;
  file_size: number;
  status: 'pending' | 'analyzing' | 'processing' | 'completed' | 'failed' | 'cancelled';
  total_rows: number | null;
  total_batches: number | null;
  batch_size: number;
  processed_rows: number;
  successful_signals: number;
  failed_signals: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  progress_percent: number | null;
}

interface ImportBatch {
  id: string;
  batch_number: number;
  start_row: number;
  end_row: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'skipped';
  processed_signals: number;
  successful_signals: number;
  failed_signals: number;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
}

interface RecentSignal {
  id: string;
  customer_name: string;
  verbatim: string;
  created_at: string;
  status: string | null;
}

interface ImportDetailResponse {
  session: ImportSession;
  batches: ImportBatch[];
  batch_summary: Record<string, number>;
  recent_signals: RecentSignal[];
}

const AdminImportDetailPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [importDetail, setImportDetail] = useState<ImportDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'batches' | 'signals'>('overview');
  const [signals, setSignals] = useState<any[]>([]);
  const [signalsLoading, setSignalsLoading] = useState(false);

  const fetchImportDetail = async () => {
    if (!sessionId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const data: ImportDetailResponse = await fetchWithAuth(`/v1/admin/imports/${sessionId}`);
      setImportDetail(data);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch import details');
    } finally {
      setLoading(false);
    }
  };

  const fetchSignals = async () => {
    if (!sessionId) return;
    
    try {
      setSignalsLoading(true);
      
      const data = await fetchWithAuth(`/v1/admin/imports/${sessionId}/signals?limit=20`);
      setSignals(data.signals || []);
      
    } catch (err) {
      console.error('Failed to fetch signals:', err);
    } finally {
      setSignalsLoading(false);
    }
  };

  useEffect(() => {
    fetchImportDetail();
  }, [sessionId]);

  useEffect(() => {
    if (activeTab === 'signals' && importDetail) {
      fetchSignals();
    }
  }, [activeTab, sessionId]);

  // Only admin users can access this page
  if (!isAdminUser(user)) {
    return <Navigate to="/v4/records" replace />;
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <IconWrapper icon={FiCheckCircle} className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <IconWrapper icon={FiXCircle} className="w-4 h-4 text-red-500" />;
      case 'processing':
        return <IconWrapper icon={FiRefreshCw} className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'pending':
        return <IconWrapper icon={FiClock} className="w-4 h-4 text-yellow-500" />;
      case 'cancelled':
      case 'skipped':
        return <IconWrapper icon={FiPause} className="w-4 h-4 text-gray-500" />;
      default:
        return <IconWrapper icon={FiClock} className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
      case 'skipped':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diff = end.getTime() - start.getTime();
    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-2">
            <IconWrapper icon={FiRefreshCw} className="w-5 h-5 animate-spin text-gray-400" />
            <span className="text-gray-500">Loading import details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !importDetail) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center gap-2">
            <IconWrapper icon={FiXCircle} className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error || 'Import not found'}</span>
          </div>
        </div>
      </div>
    );
  }

  const { session, batches, batch_summary, recent_signals } = importDetail;

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate('/v3/settings/imports')}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
            <IconWrapper icon={FiArrowLeft} className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <Heading level={2} className="text-figma-2xl">Import Details</Heading>
            <div className="flex items-center gap-2 mt-1">
              <IconWrapper icon={FiFileText} className="w-4 h-4 text-gray-500" />
              <span className="text-figma-sm text-gray-600">{session.filename}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Status Overview */}
      <div className="bg-white rounded-lg border p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {getStatusIcon(session.status)}
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(session.status)}`}>
              {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
            </span>
          </div>
          <div className="text-sm text-gray-500">
            ID: {session.session_id}
          </div>
        </div>

        {/* Progress Bar */}
        {session.progress_percent !== null && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Progress</span>
              <span className="text-sm text-gray-500">{Math.round(session.progress_percent)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-figma-blue h-3 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(session.progress_percent, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{session.total_rows || 0}</div>
            <div className="text-sm text-gray-500">Total Rows</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{session.successful_signals}</div>
            <div className="text-sm text-gray-500">Successful</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{session.failed_signals}</div>
            <div className="text-sm text-gray-500">Failed</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{session.total_batches || 0}</div>
            <div className="text-sm text-gray-500">Batches</div>
          </div>
        </div>

        {/* Error Message */}
        {session.error_message && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <IconWrapper icon={FiAlertCircle} className="w-5 h-5 text-red-500 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{session.error_message}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="border-b">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overview' 
                  ? 'border-figma-blue text-figma-blue bg-blue-50' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('batches')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'batches' 
                  ? 'border-figma-blue text-figma-blue bg-blue-50' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              Batches ({batches.length})
            </button>
            <button
              onClick={() => setActiveTab('signals')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'signals' 
                  ? 'border-figma-blue text-figma-blue bg-blue-50' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              Signals ({session.successful_signals + session.failed_signals})
            </button>
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* File Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">File Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">Filename</div>
                    <div className="font-medium">{session.filename}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">File Size</div>
                    <div className="font-medium">{formatFileSize(session.file_size)}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">File Type</div>
                    <div className="font-medium">{session.file_type}</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">Batch Size</div>
                    <div className="font-medium">{session.batch_size} rows per batch</div>
                  </div>
                </div>
              </div>

              {/* Timing Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Timing</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm text-gray-500">Created</div>
                    <div className="font-medium">
                      <Timestamp value={session.created_at} format="absolute" />
                    </div>
                  </div>
                  {session.started_at && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500">Started</div>
                      <div className="font-medium">
                        <Timestamp value={session.started_at} format="absolute" />
                      </div>
                    </div>
                  )}
                  {session.completed_at && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500">Completed</div>
                      <div className="font-medium">
                        <Timestamp value={session.completed_at} format="absolute" />
                      </div>
                    </div>
                  )}
                  {session.started_at && session.completed_at && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="text-sm text-gray-500">Duration</div>
                      <div className="font-medium">{formatDuration(session.started_at, session.completed_at)}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Batch Summary */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Batch Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {Object.entries(batch_summary).map(([status, count]) => (
                    <div key={status} className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-center mb-2">
                        {getStatusIcon(status)}
                      </div>
                      <div className="text-xl font-bold text-gray-900">{count}</div>
                      <div className="text-sm text-gray-500 capitalize">{status}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Signals Preview */}
              {recent_signals.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Signals</h3>
                  <div className="space-y-3">
                    {recent_signals.map((signal) => (
                      <div key={signal.id} className="p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 mb-1">{signal.customer_name}</div>
                            <div className="text-sm text-gray-600 line-clamp-2">{signal.verbatim}</div>
                          </div>
                          <div className="text-xs text-gray-500 ml-4">
                            <Timestamp value={signal.created_at} format="absolute" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={() => setActiveTab('signals')}
                      className="text-figma-blue hover:text-blue-700 text-sm font-medium"
                    >
                      View all signals →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Batches Tab */}
          {activeTab === 'batches' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Processing Batches</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rows</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signals</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {batches.map((batch) => (
                      <tr key={batch.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <IconWrapper icon={FiLayers} className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">#{batch.batch_number}</span>
                          </div>
                          <div className="text-sm text-gray-500">
                            Rows {batch.start_row}-{batch.end_row}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(batch.status)}
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(batch.status)}`}>
                              {batch.status}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-900">
                          {batch.end_row - batch.start_row}
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm">
                            <div className="flex items-center gap-1 text-green-600">
                              <IconWrapper icon={FiCheckCircle} className="w-3 h-3" />
                              {batch.successful_signals}
                            </div>
                            {batch.failed_signals > 0 && (
                              <div className="flex items-center gap-1 text-red-600">
                                <IconWrapper icon={FiXCircle} className="w-3 h-3" />
                                {batch.failed_signals}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500">
                          {batch.started_at && batch.completed_at 
                            ? formatDuration(batch.started_at, batch.completed_at)
                            : '-'
                          }
                        </td>
                        <td className="px-4 py-4">
                          {batch.error_message ? (
                            <div className="flex items-start gap-1">
                              <IconWrapper icon={FiAlertCircle} className="w-4 h-4 text-red-500 mt-0.5" />
                              <span className="text-sm text-red-600 max-w-xs truncate" title={batch.error_message}>
                                {batch.error_message}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Signals Tab */}
          {activeTab === 'signals' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Created Signals</h3>
                <Link
                  to={`/v4/records?import_session=${session.session_id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-figma-blue text-white rounded-md hover:bg-blue-600 transition-colors"
                >
                  <IconWrapper icon={FiEye} className="w-4 h-4" />
                  View in Signals Live
                </Link>
              </div>
              
              {signalsLoading ? (
                <div className="text-center py-8">
                  <IconWrapper icon={FiRefreshCw} className="w-5 h-5 animate-spin text-gray-400 mx-auto mb-2" />
                  <span className="text-gray-500">Loading signals...</span>
                </div>
              ) : signals.length === 0 ? (
                <div className="text-center py-8">
                  <IconWrapper icon={FiUsers} className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <span className="text-gray-500">No signals found</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {signals.map((signal) => (
                    <div key={signal.id} className="p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium text-gray-900">{signal.customer_name}</span>
                            {signal.customer_email && (
                              <span className="text-sm text-gray-500">({signal.customer_email})</span>
                            )}
                            {signal.status && (
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(signal.status)}`}>
                                {signal.status}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 mb-2">{signal.verbatim}</div>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>Channel: {signal.channel}</span>
                            <span>Severity: {signal.initial_severity}%</span>
                            {signal.complaint_detected && (
                              <span className="text-red-600">Complaint Detected</span>
                            )}
                            {signal.vulnerability_detected && (
                              <span className="text-orange-600">Vulnerability Detected</span>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 ml-4">
                          <Timestamp value={signal.created_at} format="absolute" />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {signals.length >= 20 && (
                    <div className="text-center pt-4">
                      <Link
                        to={`/v4/records?import_session=${session.session_id}`}
                        className="text-figma-blue hover:text-blue-700 text-sm font-medium"
                      >
                        View all {session.successful_signals + session.failed_signals} signals →
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminImportDetailPage;